import apiClient from "@/api-client";
import { downloadBlob } from "@/utils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { logger } from "@/lib/default-logger";

export type CampaignBriefStatus =
	| "draft"
	| "submitted"
	| "in_review"
	| "scheduled"
	| "live"
	| "completed"
	| "cancelled";

export type BriefAgentChoice = "existing" | "new";

export type BriefDocumentKind = "call_flow" | "lead_list" | "po" | "sow";

/** Dialling hours on one weekday, local to each region: campaigns' own scheduling shape. */
export interface BriefWindow {
	/** ISO weekday, Monday is 1. */
	dow: number;
	from: string;
	to: string;
}

export interface CampaignBrief {
	id: string;
	title: string;
	dialDate: string;
	redialDates: string[];
	schedule: BriefWindow[];
	agentChoice: BriefAgentChoice;
	agentId: string | null;
	agentName: string | null;
	regions: string[];
	languages: string[];
	poRequired: boolean;
	sowRequired: boolean;
	notes: string | null;
	addOns: BriefAddOn[];
	status: CampaignBriefStatus;
	submittedAt: string | null;
	submittedByName: string | null;
	createdAt: string;
	updatedAt: string;
}

/** An optional service the client ticked, with the rate card price it had at the time. */
export interface BriefAddOn {
	code: string;
	name: string;
	amount: string | null;
	currency: string;
	unit: string | null;
	priceNote: string | null;
}

export interface CampaignBriefDocument {
	id: string;
	briefId: string;
	kind: BriefDocumentKind;
	originalFilename: string;
	mimeType: string;
	sizeBytes: number;
	createdAt: string;
}

export interface CampaignBriefEvent {
	id: string;
	action: "status_changed" | "document_downloaded";
	fromStatus: CampaignBriefStatus | null;
	toStatus: CampaignBriefStatus | null;
	documentId: string | null;
	actorName: string | null;
	note: string | null;
	at: string;
}

export interface CampaignBriefDetail {
	brief: CampaignBrief;
	documents: CampaignBriefDocument[];
	events: CampaignBriefEvent[];
	submittedByEmail: string | null;
	accountName: string | null;
}

/** One row of the super-admin list, across every account. */
export interface AdminCampaignBrief {
	briefId: string;
	accountId: string | null;
	accountName: string | null;
	title: string;
	status: CampaignBriefStatus;
	regions: string[];
	languages: string[];
	dialDate: string;
	redialDates: string[];
	schedule: BriefWindow[];
	agentChoice: BriefAgentChoice;
	agentName: string | null;
	poRequired: boolean;
	sowRequired: boolean;
	hasPo: boolean;
	hasSow: boolean;
	submittedAt: string | null;
	submittedByName: string | null;
	updatedAt: string;
}

/** One dial slot on the super-admin calendar: a first dial or a redial, local to each region. */
export interface BriefCalendarEvent {
	briefId: string;
	accountName: string | null;
	title: string;
	status: CampaignBriefStatus;
	date: string;
	/** That weekday's dialling hours, earliest first. */
	hours: BriefWindow[];
	regions: string[];
	kind: "dial" | "redial";
}

export interface SaveCampaignBriefPayload {
	title: string;
	dialDate: string;
	redialDates: string[];
	schedule: BriefWindow[];
	agentChoice: BriefAgentChoice;
	agentId: string | null;
	regions: string[];
	languages: string[];
	poRequired: boolean;
	sowRequired: boolean;
	notes: string | null;
	/** Rate card codes of the optional services ticked. */
	addOns: string[];
}

export const campaignBriefsKeys = {
	all: ["campaignBriefs"] as const,
	lists: () => [...campaignBriefsKeys.all, "list"] as const,
	detail: (id: string) => [...campaignBriefsKeys.all, "detail", id] as const,
	adminLists: () => [...campaignBriefsKeys.all, "admin", "list"] as const,
	adminDetail: (id: string) => [...campaignBriefsKeys.all, "admin", "detail", id] as const,
	adminCalendar: (from: string, to: string, includeCancelled: boolean) =>
		[...campaignBriefsKeys.all, "admin", "calendar", from, to, includeCancelled] as const,
	clientCalendar: (from: string, to: string, includeCancelled: boolean) =>
		[...campaignBriefsKeys.all, "calendar", from, to, includeCancelled] as const,
};

export function briefErrorMessage(error: any, fallback: string): string {
	const message = error?.response?.data?.message ?? error?.response?.data?.errors;
	if (Array.isArray(message)) return message.join(" ");
	return message || fallback;
}

export function useCampaignBrief(id: string | undefined) {
	return useQuery({
		queryKey: campaignBriefsKeys.detail(id ?? "none"),
		queryFn: async (): Promise<CampaignBriefDetail> => {
			const response = await apiClient.get(`/campaign-briefs/${id}`);
			return response.data;
		},
		enabled: Boolean(id),
	});
}

export function useAdminCampaignBrief(id: string | undefined) {
	return useQuery({
		queryKey: campaignBriefsKeys.adminDetail(id ?? "none"),
		queryFn: async (): Promise<CampaignBriefDetail> => {
			const response = await apiClient.get(`/admin/campaign-briefs/${id}`);
			return response.data;
		},
		enabled: Boolean(id),
	});
}

/** Every account's dial slots dated `from` to `to`, both inclusive. Waits until the calendar knows its range. */
export function useAdminCampaignBriefCalendar(range: { from: string; to: string } | null, includeCancelled: boolean) {
	return useQuery({
		queryKey: campaignBriefsKeys.adminCalendar(range?.from ?? "", range?.to ?? "", includeCancelled),
		queryFn: async (): Promise<BriefCalendarEvent[]> => {
			const response = await apiClient.get(`/admin/campaign-briefs/calendar`, {
				params: { ...range, ...(includeCancelled ? { includeCancelled: true } : {}) },
			});
			return response.data;
		},
		enabled: Boolean(range),
		// Keeps last month's entries up while next month's load, rather than flashing an empty grid.
		placeholderData: keepPreviousData,
	});
}

/** The same calendar for the signed-in client's own account. */
export function useClientCampaignBriefCalendar(range: { from: string; to: string } | null, includeCancelled: boolean) {
	return useQuery({
		queryKey: campaignBriefsKeys.clientCalendar(range?.from ?? "", range?.to ?? "", includeCancelled),
		queryFn: async (): Promise<BriefCalendarEvent[]> => {
			const response = await apiClient.get(`/campaign-briefs/calendar`, {
				params: { ...range, ...(includeCancelled ? { includeCancelled: true } : {}) },
			});
			return response.data;
		},
		enabled: Boolean(range),
		// Keeps last month's entries up while next month's load, rather than flashing an empty grid.
		placeholderData: keepPreviousData,
	});
}

export interface SubmitCampaignBriefInput {
	/** Set when an earlier attempt already saved the draft, so a retry does not start another. */
	draftId: string | null;
	payload: SaveCampaignBriefPayload;
	files: Partial<Record<BriefDocumentKind, File>>;
	/** Files that already reached the draft on an earlier attempt, by kind, so they are not sent again. */
	uploaded: Partial<Record<BriefDocumentKind, File>>;
	onDraft: (id: string) => void;
	onUploaded: (kind: BriefDocumentKind, file: File) => void;
	onStep: (step: string) => void;
}

/**
 * Save the draft, attach each file, then hand it over. Uploads need a request id, so the draft comes
 * first; if any step fails the draft is kept and a retry carries on from where it stopped.
 */
export async function submitCampaignBrief(input: SubmitCampaignBriefInput): Promise<CampaignBrief> {
	let id = input.draftId;
	input.onStep("Saving your request");
	if (id) {
		await apiClient.put(`/campaign-briefs/${id}`, input.payload);
	} else {
		const response = await apiClient.post(`/campaign-briefs`, input.payload);
		id = (response.data as CampaignBrief).id;
		input.onDraft(id);
	}

	for (const [kind, file] of Object.entries(input.files) as [BriefDocumentKind, File][]) {
		if (!file || input.uploaded[kind] === file) continue;
		input.onStep(`Uploading ${file.name}`);
		await uploadBriefDocument(id, kind, file);
		input.onUploaded(kind, file);
	}

	input.onStep("Submitting");
	const response = await apiClient.post(`/campaign-briefs/${id}/submit`);
	return response.data;
}

export async function uploadBriefDocument(briefId: string, kind: BriefDocumentKind, file: File) {
	const formData = new FormData();
	formData.append("file", file);
	// The kind is in the path: the API's validation ignores fields inside a multipart body.
	const response = await apiClient.post(`/campaign-briefs/${briefId}/documents/${kind}`, formData, {
		headers: { "Content-Type": "multipart/form-data" },
	});
	return response.data as CampaignBriefDocument;
}

export function useSubmitCampaignBrief() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: submitCampaignBrief,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: campaignBriefsKeys.all });
			toast.success("Request submitted. We'll be in touch.");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(briefErrorMessage(error, "Could not submit the request"));
		},
	});
}

/** A PO or SOW sent after the request was submitted. */
export function useUploadBriefPaperwork(briefId: string) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ kind, file }: { kind: BriefDocumentKind; file: File }) =>
			await uploadBriefDocument(briefId, kind, file),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: campaignBriefsKeys.all });
			toast.success("File uploaded");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(briefErrorMessage(error, "Could not upload the file"));
		},
	});
}

export function useWithdrawCampaignBrief() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string) => {
			const response = await apiClient.post(`/campaign-briefs/${id}/withdraw`);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: campaignBriefsKeys.all });
			toast.success("Request withdrawn");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(briefErrorMessage(error, "Could not withdraw the request"));
		},
	});
}

export function useDeleteCampaignBriefDraft() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string) => await apiClient.delete(`/campaign-briefs/${id}`),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: campaignBriefsKeys.all });
			toast.success("Draft deleted");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(briefErrorMessage(error, "Could not delete the draft"));
		},
	});
}

export function useMoveCampaignBrief(briefId: string) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: { status: CampaignBriefStatus; note: string | null }) => {
			const response = await apiClient.put(`/admin/campaign-briefs/${briefId}/status`, data);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: campaignBriefsKeys.all });
			toast.success("Status updated");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(briefErrorMessage(error, "Could not update the status"));
		},
	});
}

/** Fetched with the session's token and saved as a file; never opened in the browser. */
export async function downloadBriefDocument(url: string, filename: string): Promise<void> {
	try {
		const response = await apiClient.get(url, { responseType: "blob" });
		downloadBlob(response.data as Blob, filename);
	} catch (error) {
		logger.error(error);
		toast.error("Could not download the file");
	}
}
